import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tag, Plus, Trash2, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import useFeatureGating from '../../hooks/useFeatureGating';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DiscountCodesPanel({ eventId, event, discountCodes, onRefresh }) {
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [discountForm, setDiscountForm] = useState({ code: '', discount_type: 'percentage', value: '', applicable_tiers: [], usage_limit: '', expiration: '' });
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [editingDiscountId, setEditingDiscountId] = useState(null);
  const { canUse } = useFeatureGating();
  const canUseDiscountCodes = canUse('discount_codes');

  const resetForm = () => setDiscountForm({ code: '', discount_type: 'percentage', value: '', applicable_tiers: [], usage_limit: '', expiration: '' });

  const handleCreateDiscount = async (e) => {
    e.preventDefault();
    if (!discountForm.code || !discountForm.value) { toast.error('Code and value are required'); return; }
    setSavingDiscount(true);
    try {
      const token = localStorage.getItem('session_token');
      const payload = {
        code: discountForm.code,
        discount_type: discountForm.discount_type,
        value: parseFloat(discountForm.value),
        applicable_tiers: discountForm.applicable_tiers,
        usage_limit: discountForm.usage_limit ? parseInt(discountForm.usage_limit) : null,
        expiration: discountForm.expiration || null
      };
      if (editingDiscountId) {
        await axios.put(`${API}/events/${eventId}/discount-codes/${editingDiscountId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Discount code updated');
      } else {
        await axios.post(`${API}/events/${eventId}/discount-codes`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Discount code created');
      }
      resetForm();
      setShowDiscountForm(false);
      setEditingDiscountId(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSavingDiscount(false); }
  };

  const handleEditDiscount = (dc) => {
    setDiscountForm({
      code: dc.code,
      discount_type: dc.discount_type,
      value: dc.value.toString(),
      applicable_tiers: dc.applicable_tiers || [],
      usage_limit: dc.usage_limit ? dc.usage_limit.toString() : '',
      expiration: dc.expiration ? dc.expiration.split('T')[0] : ''
    });
    setEditingDiscountId(dc.code_id);
    setShowDiscountForm(true);
  };

  const handleToggleDiscount = async (codeId, active) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/events/${eventId}/discount-codes/${codeId}`, { active: !active }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(active ? 'Deactivated' : 'Activated');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteDiscount = async (codeId) => {
    if (!confirm('Delete this discount code?')) return;
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/events/${eventId}/discount-codes/${codeId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid="discount-codes-section">
      <div className="p-4 border-b border-[#1A1A1A] flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-[#EAE7E1] flex items-center">
            <Tag className="w-5 h-5 mr-2 text-[#D4AF37]" />Discount Codes
            {!canUseDiscountCodes && (
              <span className="ml-2 text-[9px] uppercase tracking-widest text-[#D4AF37]/80 border border-[#D4AF37]/25 rounded-sm px-1.5 py-0.5" data-testid="discount-codes-pro-label">Pro</span>
            )}
          </h2>
          <p className="text-xs text-[#777] mt-1">{discountCodes.length} code(s)</p>
        </div>
        {canUseDiscountCodes ? (
          <Button onClick={() => { setShowDiscountForm(!showDiscountForm); if (showDiscountForm) { setEditingDiscountId(null); resetForm(); } }} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" data-testid="add-discount-button"><Plus className="w-4 h-4 mr-2" />New Code</Button>
        ) : (
          <Button disabled title="Pro feature" variant="outline" className="border-[#1A1A1A] text-[#555] rounded-sm cursor-not-allowed" data-testid="add-discount-button-locked"><Lock className="w-4 h-4 mr-2" />New Code</Button>
        )}
      </div>
      {canUseDiscountCodes && showDiscountForm && (
        <div className="p-4 border-b border-[#1A1A1A] bg-[#050505]">
          <form onSubmit={handleCreateDiscount} className="space-y-3" data-testid="discount-code-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Code *</Label>
                <Input value={discountForm.code} onChange={(e) => setDiscountForm({ ...discountForm, code: e.target.value.toUpperCase() })} placeholder="e.g., VIP20" required className="mt-1 uppercase" data-testid="discount-code-name-input" />
              </div>
              <div>
                <Label>Type *</Label>
                <select value={discountForm.discount_type} onChange={(e) => setDiscountForm({ ...discountForm, discount_type: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-sm" data-testid="discount-type-select">
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ($)</option>
                </select>
              </div>
              <div>
                <Label>Value *</Label>
                <Input type="number" step="0.01" min="0" value={discountForm.value} onChange={(e) => setDiscountForm({ ...discountForm, value: e.target.value })} placeholder={discountForm.discount_type === 'percentage' ? 'e.g., 20' : 'e.g., 10'} required className="mt-1" data-testid="discount-value-input" />
              </div>
              <div>
                <Label>Usage Limit</Label>
                <Input type="number" min="1" value={discountForm.usage_limit} onChange={(e) => setDiscountForm({ ...discountForm, usage_limit: e.target.value })} placeholder="Unlimited" className="mt-1" data-testid="discount-limit-input" />
              </div>
              <div>
                <Label>Expiration</Label>
                <Input type="date" value={discountForm.expiration} onChange={(e) => setDiscountForm({ ...discountForm, expiration: e.target.value })} className="mt-1" data-testid="discount-expiration-input" />
              </div>
              {event?.pass_tiers?.length > 0 && (
                <div>
                  <Label>Applicable Tiers</Label>
                  <div className="mt-1 space-y-1">
                    {event.pass_tiers.map(t => (
                      <label key={t.id} className="flex items-center space-x-2 text-sm text-[#AFAFAF] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={discountForm.applicable_tiers.includes(t.id)}
                          onChange={(e) => {
                            const tiers = e.target.checked ? [...discountForm.applicable_tiers, t.id] : discountForm.applicable_tiers.filter(x => x !== t.id);
                            setDiscountForm({ ...discountForm, applicable_tiers: tiers });
                          }}
                          className="rounded"
                        />
                        <span>{t.tier_name}</span>
                      </label>
                    ))}
                    <p className="text-xs text-[#555]">Leave unchecked = applies to all tiers</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex space-x-3 pt-2">
              <Button type="submit" disabled={savingDiscount} className="bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm px-6 font-bold" data-testid="save-discount-button">{savingDiscount ? 'Saving...' : editingDiscountId ? 'Update Code' : 'Create Code'}</Button>
              <Button type="button" onClick={() => { setShowDiscountForm(false); setEditingDiscountId(null); resetForm(); }} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] rounded-sm">Cancel</Button>
            </div>
          </form>
        </div>
      )}
      {discountCodes.length === 0 ? <div className="p-6 text-center text-[#555] text-sm">No discount codes</div> : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#050505] border-b border-[#1A1A1A]"><tr>{['Code','Type','Value','Tiers','Used','Limit','Expires','Status','Actions'].map(h => <th key={h} className="px-3 py-3 text-left text-xs font-medium text-[#777] uppercase">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-[#1A1A1A]">{discountCodes.map(dc => {
              const tierNames = (dc.applicable_tiers || []).length > 0
                ? dc.applicable_tiers.map(tid => event?.pass_tiers?.find(t => t.id === tid)?.tier_name || tid).join(', ')
                : 'All';
              return (
              <tr key={dc.code_id} data-testid={`discount-row-${dc.code_id}`}>
                <td className="px-3 py-3 text-sm font-mono font-bold text-[#EAE7E1]">{dc.code}</td>
                <td className="px-3 py-3 text-sm text-[#AFAFAF]">{dc.discount_type === 'percentage' ? '%' : '$'}</td>
                <td className="px-3 py-3 text-sm text-[#EAE7E1] font-medium">{dc.discount_type === 'percentage' ? `${dc.value}%` : `$${dc.value}`}</td>
                <td className="px-3 py-3 text-xs text-[#AFAFAF] max-w-[120px] truncate" title={tierNames}>{tierNames}</td>
                <td className="px-3 py-3 text-sm text-[#AFAFAF]">{dc.usage_count}</td>
                <td className="px-3 py-3 text-sm text-[#AFAFAF]">{dc.usage_limit || '\u221E'}</td>
                <td className="px-3 py-3 text-sm text-[#AFAFAF]">{dc.expiration ? new Date(dc.expiration).toLocaleDateString() : '\u2014'}</td>
                <td className="px-3 py-3"><span className={`px-2 py-1 text-xs font-medium rounded-sm ${dc.active ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-[#555]/20 text-[#555]'}`}>{dc.active ? 'ACTIVE' : 'OFF'}</span></td>
                <td className="px-3 py-3 text-sm space-x-1 whitespace-nowrap">
                  <Button onClick={() => handleEditDiscount(dc)} size="sm" variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-sm" data-testid={`edit-discount-${dc.code_id}`}>Edit</Button>
                  <Button onClick={() => handleToggleDiscount(dc.code_id, dc.active)} size="sm" variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-sm" data-testid={`toggle-discount-${dc.code_id}`}>{dc.active ? 'Off' : 'On'}</Button>
                  <Button onClick={() => handleDeleteDiscount(dc.code_id)} size="sm" variant="ghost" className="text-[#555] hover:text-[#C44545]" data-testid={`delete-discount-${dc.code_id}`}><Trash2 className="w-3 h-3" /></Button>
                </td>
              </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
